import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  InputAdornment,
  Typography,
  CircularProgress,
  useTheme
} from '@mui/material';

import IconButton from 'components/@extended/IconButton';
import Stack from '@mui/material/Stack';
import InputLabel from '@mui/material/InputLabel';
import { FormikProvider, useFormik, Form } from 'formik';
import * as Yup from 'yup';
import {
  useInsertUserRecord,
  useUpdateUserRecord,
  useGetUserModal,
  useUpdateProfilePic,
  useGetProfileImageUrl,
  endpoints,
  useDisableTwoFA,
  useSendPasswordInvite
} from 'api/user';
import { Box } from '@mui/system';
import { strengthColor, strengthIndicator } from 'utils/password-strength';
import { useState, SyntheticEvent, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { CloseOutlined, SaveOutlined, CameraOutlined, UserOutlined, UploadOutlined } from '@ant-design/icons';
import { GetFormattedDate, GetFormattedTimeOnly } from 'utils/commonFunctions';
import { useCheckDuplicateEmail } from 'api/user';
import { PPF_USER_TYPE_OPTIONS, PPFUserType, User, UserList } from 'types/user';
import { alpha } from '@mui/material/styles';
import FormLabel from '@mui/material/FormLabel';
import { isEqual } from 'lodash-es';
import { mutate } from 'swr';
import RoleLookup from '../role/RoleLookup';
import { StringColorProps } from 'types/password';
import MainCard from 'components/MainCard';
import UserTypeLookup from './UserTypeLookup';
import RemoveRoleConfirmation from './RemoveRoleConfirmation';
import useAuth from 'hooks/useAuth';
import Lookup from 'components/controls/Lookup';
import CommonPhoneInput from 'components/controls/CommonPhoneInput';
import UserLookup from './UserLookup';

export interface UserFormValues {
  userID?: number;
  fullName: string;
  emailID: string;
  phone?: string;
  userRoleIDs: number[];
  active: boolean;
  twoFAEnabled?: boolean;
  isSalesRep?: boolean;
  defaultCommissionPerc?: string | number | null;
  ppfUserType: number[]
  createdOn?: string;
  createdByUserName?: string;
  updatedOn?: string;
  updatedByUserName?: string;
  supervisorUserID?: number;
  dueTaskEmailNotification?: number;
  licenseNo?: string;
}

interface UserFormProps {
  user?: UserList | null;
  closeModal: (state: boolean) => void;
  onUserCreated?: (newUser: User) => void;
  currentUser?: any;
  defaultUserType?: number;
}

export const toppfUserTypeFlag = (values: number[]) =>
  values.reduce((sum, v) => sum | v, 0);

export const fromppfUserTypeFlag = (flag: number | string | null | undefined): number[] => {
  if (!flag) return [];
  if (typeof flag === 'string') {
    const parts = flag.split(',').map((s) => s.trim().toLowerCase());
    return PPF_USER_TYPE_OPTIONS.filter((o) =>
      parts.some((p) => p === o.userType.toLowerCase() || p === o.userType.toLowerCase().replace(/\s+/g, ''))
    ).map((o) => o.value);
  }
  return PPF_USER_TYPE_OPTIONS.filter((o) => (flag & o.value) !== 0).map((o) => o.value);
};

export const dueTaskEmailNotificationOptions = [
  { value: 0, label: 'Never' },
  { value: 1, label: 'Everyday' },
  { value: 2, label: 'Once a week' }
];

const UserForm = ({ user, closeModal, onUserCreated, currentUser, defaultUserType }: UserFormProps) => {
  const { user: LoggedInUser } = useAuth();
  const shouldFetch = Boolean(user);
  const userID = user?.userID;
  const { data: latestUser } = useGetUserModal(shouldFetch ? userID : null);
  // Use ref to store initial values for comparison
  const initialValuesRef = useRef<any>(null);
  // Use ref to track initial profile picture
  const initialProfilePicRef = useRef<string | null>(null);
  const [openDeleteRoleConfirmation, setOpenDeleteRoleConfirmation] = useState(false);
  const [level, setLevel] = useState<StringColorProps>();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const theme = useTheme();
  const isClosingRef = useRef(false);

  const timerRef = useRef<any>(null);
  const lastCheckedNameRef = useRef<string>('');
  const checkDuplicatePromiseRef = useRef<Promise<boolean> | null>(null);

  const [isDuplicate, setIsDuplicate] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);

  if (shouldFetch) {
    var { data: profilePicUrl } = useGetProfileImageUrl(Number(userID));
  }

  const checkDuplicate = useCallback(
    async (email: string) => {
      try {
        setDuplicateError(false);
        lastCheckedNameRef.current = email;

        const promise = useCheckDuplicateEmail(email, userID);
        checkDuplicatePromiseRef.current = promise;

        const res = await promise;

        setIsDuplicate(res);

        return res;
      } catch (error) {
        setDuplicateError(true);
        lastCheckedNameRef.current = '';
        return false;
      } finally {
        checkDuplicatePromiseRef.current = null;
      }
    },
    [userID]
  );

  const changePassword = (value: string) => {
    const temp = strengthIndicator(value);
    setLevel(strengthColor(temp));
  };

  const UserSchema = Yup.object().shape({
    fullName: Yup.string().trim().required('First name is required').max(100, 'Full name must be at most 100 characters'),
    emailID: Yup.string()
      .trim()
      .required('Email ID is required')
      .email('Enter a valid email ID')
      .max(100, 'Email ID must be at most 100 characters')
      .matches(/^[^@]+@[^@]+\.[^@]+$/, 'Enter a valid email ID'),
    phone: Yup.string()
      .trim()
      .nullable()
      .test('valid-phone', 'Enter a valid 10-digit phone number', (val) => {
        if (!val || val.trim() === '') return true;
        const cleanDigits = val.replace(/\D/g, '');
        return cleanDigits.length === 10;
      }),
    userRoleIDs: Yup.array().of(Yup.number()).nullable().required('Role is required'),
    active: Yup.boolean().oneOf([true, false]),
    ppfUserType: currentUser?.userType === 2 ? Yup.array().of(Yup.number()).nullable()
      .test(
        'sales-rep-required',
        'Sales Rep user type is required',
        function (value) {
          if (defaultUserType === PPFUserType.SalesRep) {
            return value?.includes(PPFUserType.SalesRep);
          }
          return true;
        }
      )
      : Yup.array().optional().nullable(),
    defaultCommissionPerc: Yup.number()
      .nullable()
      .min(0, 'Commission cannot be negative')
      .max(100, 'Commission cannot exceed 100%'),
    supervisorUserID: Yup.number().nullable().optional(),
    dueTaskEmailNotification: Yup.number().nullable().optional(),
    licenseNo: Yup.string().when('ppfUserType', {
      is: (userTypes: number[] | null | undefined) => userTypes?.includes(PPFUserType.PrivateProvider),
      then: (schema) => schema.trim().required('License Number is required').max(50, 'License Number must not exceed 50 characters'),
      otherwise: (schema) => schema.nullable()
    }),
  });

  const getInitialFormValues = (userData: any): UserFormValues => {
    if (userData) {
      return {
        fullName: userData.fullName || '',
        emailID: userData.emailID || '',
        active: userData.active ?? true,
        userRoleIDs: userData.userRoleIDs,
        phone: userData.phone || '',
        twoFAEnabled: currentUser.twoFAEnabled ?? false,
        isSalesRep: userData.isSalesRep || (userData.ppfUserType !== undefined && userData.ppfUserType !== null && (userData.ppfUserType & PPFUserType.SalesRep) !== 0),
        defaultCommissionPerc: userData.defaultCommissionPerc ? (userData.defaultCommissionPerc * 100).toFixed(2) : '0.00',
        ppfUserType: currentUser?.userType === 2 ? fromppfUserTypeFlag(userData.ppfUserType || 0) : [],
        userID: userData.userID,
        createdOn: userData.createdOn,
        createdByUserName: userData.createdByUserName,
        updatedOn: userData.updatedOn,
        updatedByUserName: userData.updatedByUserName,
        supervisorUserID: userData.supervisorUserID,
        dueTaskEmailNotification: userData.dueTaskEmailNotification,
        licenseNo: userData.licenseNo || userData.LicenseNo || ''
      };

    } else {
      const initialPPFUserType = defaultUserType ? [defaultUserType] : [];
      return {
        fullName: '',
        emailID: '',
        phone: '',
        userRoleIDs: [],
        ppfUserType: initialPPFUserType,
        active: true,
        twoFAEnabled: false,
        isSalesRep: initialPPFUserType.includes(PPFUserType.SalesRep),
        defaultCommissionPerc: '0.00',
        supervisorUserID: undefined,
        dueTaskEmailNotification: 0,
        licenseNo: ''
      };
    }
  };

  const formik = useFormik<UserFormValues>({
    initialValues: getInitialFormValues(latestUser),
    validationSchema: UserSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      const trimmedVal = values.emailID.trim();
      const originalVal = (latestUser?.emailID || '').trim();

      if (timerRef.current) clearTimeout(timerRef.current);

      let duplicate = isDuplicate;

      if (checkDuplicatePromiseRef.current) {
        duplicate = await checkDuplicatePromiseRef.current;
      } else if (trimmedVal !== originalVal && trimmedVal !== lastCheckedNameRef.current) {
        duplicate = await checkDuplicate(trimmedVal);
      }

      if (duplicate) {
        setSubmitting(false);
        return;
      }

      const isPrivateProviderSelected = values.ppfUserType?.includes(PPFUserType.PrivateProvider);

      const payload: any = {
        fullName: values.fullName.trim(),
        emailID: values.emailID.trim(),
        phone: values.phone,
        active: values.active,
        updatedOn: values.updatedOn,
        licenseNo: isPrivateProviderSelected && values.licenseNo ? values.licenseNo.trim() : (values.licenseNo?.trim() || null),
        ...(currentUser?.userType === 2 && {
          ppfUserType: values.ppfUserType && values.ppfUserType.length > 0 ? toppfUserTypeFlag(values.ppfUserType) : null,
          supervisorUserID: values.supervisorUserID,
          dueTaskEmailNotification: values.dueTaskEmailNotification
        }),
        userID: values.userID,
        UserRoleIDs: values.userRoleIDs,
        TwoFAEnabled: values.twoFAEnabled
      };
      if (currentUser.userType === 2) {
        payload.isSalesRep = values.isSalesRep;
        payload.defaultCommissionPerc = Number(values.defaultCommissionPerc) / 100 || 0;
      }
      try {
        let response: any;
        let effectiveUserID: number;

        if (userID) {
          response = await useUpdateUserRecord(Number(payload.userID), payload);
          // Check if 2FA was disabled
          if (initialValuesRef.current?.twoFAEnabled && !values.twoFAEnabled) {
            await useDisableTwoFA(Number(userID));
            mutate(`UserManagement/CurrentUser`);
            mutate(`${endpoints.key}${endpoints.getList}`);
          }

          effectiveUserID = Number(userID);
        } else {
          // Create new user
          delete payload.updatedOn;
          delete payload.userID;

          response = await useInsertUserRecord(payload);
          effectiveUserID = (response as any)?.primaryKeyID as number;
          if (effectiveUserID) {
            useSendPasswordInvite(effectiveUserID)
          }
        }

        if (!response) {
          setSubmitting(false);
          return;
        }

        resetForm();
        closeModal(false);

        if (profilePicChanged) {
          if (profilePic === null && userDeletedPic) {
            // DELETE MODE
            useUpdateProfilePic(null, effectiveUserID)
              .then(() => {
                mutate(`${endpoints.key}/GetUserProfileImageUrl/${effectiveUserID}`);
                if (effectiveUserID === LoggedInUser?.userID) {
                  mutate('UserManagement/CurrentUser/GetUserProfileImageUrl', undefined, { revalidate: true });
                  mutate('UserManagement/CurrentUser/GetUserProfileImageThumbnailUrl', undefined, { revalidate: true });
                }
              })
              .catch((err) => {
                console.error('Profile picture delete failed:', err);
              });
          } else if (profilePic instanceof File) {
            // UPLOAD MODE
            useUpdateProfilePic(profilePic, effectiveUserID)
              .then(() => {
                mutate(`${endpoints.key}/GetUserProfileImageUrl/${effectiveUserID}`);
                if (effectiveUserID === LoggedInUser?.userID) {
                  mutate('UserManagement/CurrentUser/GetUserProfileImageUrl', undefined, { revalidate: true });
                  mutate('UserManagement/CurrentUser/GetUserProfileImageThumbnailUrl', undefined, { revalidate: true });
                }
              }).catch((err) => {
                console.error('Profile picture upload failed:', err);
              });
          }
        }

        if (effectiveUserID === LoggedInUser?.userID) {
          mutate('UserManagement/CurrentUser', undefined, { revalidate: true });
        }

        // Call onUserCreated callback for new users
        if (!user && onUserCreated) {
          onUserCreated(response as any);
        }
      } catch (err) {
        console.error('Failed to save user', err);
        setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    if (latestUser && !initialValuesRef.current) {
      const initialValues = getInitialFormValues(latestUser);
      // Store serialized version for reliable comparison
      initialValuesRef.current = {
        fullName: initialValues.fullName,
        emailID: initialValues.emailID,
        ppfUserType: initialValues?.ppfUserType,
        licenseNo: initialValues.licenseNo,
        phone: initialValues.phone,
        userRoleIDs: initialValues.userRoleIDs,
        active: initialValues.active,
        // updatePassword: initialValues.updatePassword,
        twoFAEnabled: initialValues.twoFAEnabled,
        isSalesRep: currentUser.userType === 2 ? initialValues.isSalesRep : null,
        defaultCommissionPerc: currentUser.userType === 2 ? initialValues.defaultCommissionPerc : null
      };

      // Store initial profile picture only if not already set
      if (profilePicUrl && !initialProfilePicRef.current) {
        initialProfilePicRef.current = profilePicUrl;
      }
    }
  }, [latestUser, shouldFetch, profilePicUrl]);

  // State to track if profile picture has changed
  const [profilePicChanged, setProfilePicChanged] = useState(false);

  const hasFormChanges = useCallback(() => {
    if (!userID) {
      // For new user, check if required fields are filled
      return (
        formik.values.fullName.trim().length > 0 &&
        formik.values.emailID.trim().length > 0 &&
        formik.values.userRoleIDs.length > 0

      );
    }

    if (!initialValuesRef.current) return false;

    // Serialize current values for comparison (normalize to avoid false positives)
    const currentValues = {
      fullName: formik.values.fullName?.trim() ?? '',
      emailID: formik.values.emailID?.trim() ?? '',
      phone: formik.values.phone?.trim() ?? '',
      userRoleIDs: formik.values.userRoleIDs,
      ppfUserType: formik.values?.ppfUserType,
      licenseNo: formik.values.licenseNo?.trim() ?? '',
      active: formik.values.active,
      // updatePassword: formik.values.updatePassword,
      twoFAEnabled: formik.values.twoFAEnabled,
      isSalesRep: currentUser.userType === 2 ? formik.values.isSalesRep : null,
      defaultCommissionPerc: currentUser.userType === 2 ? String(formik.values.defaultCommissionPerc ?? '0.00') : null
    };

    const normalizedInitial = {
      ...initialValuesRef.current,
      fullName: initialValuesRef.current.fullName?.trim() ?? '',
      emailID: initialValuesRef.current.emailID?.trim() ?? '',
      phone: initialValuesRef.current.phone?.trim() ?? '',
      defaultCommissionPerc: currentUser.userType === 2 ? String(initialValuesRef.current.defaultCommissionPerc ?? '0.00') : null
    };

    return !isEqual(currentValues, normalizedInitial) || profilePicChanged;
  }, [formik.values, userID, profilePicChanged]);

  const handleClose = () => {
    closeModal(false);
    formik.resetForm();
  };

  const { errors, touched, getFieldProps, handleSubmit, setFieldValue, values, submitCount, isSubmitting, handleBlur } = formik;

  // useEffect(() => {
  //   changePassword(values.password || '');
  // }, [values.password]);

  // Debounce API call 5 seconds after user stops typing
  useEffect(() => {
    const trimmedVal = formik.values.emailID.trim();
    const originalVal = (latestUser?.emailID || '').trim();

    if (!trimmedVal || trimmedVal === originalVal || errors.emailID) {
      setIsDuplicate(false);
      return;
    }

    if (trimmedVal !== lastCheckedNameRef.current) {
      setIsDuplicate(false);
      setDuplicateError(false);
      lastCheckedNameRef.current = '';
    }

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      checkDuplicate(trimmedVal);
    }, 3000);

    return () => clearTimeout(timerRef.current);
  }, [values.emailID, errors.emailID, latestUser, checkDuplicate]);

  // Custom validation for duplicate email
  const getEmailError = useCallback(() => {
    if (touched.emailID && errors.emailID) {
      return errors.emailID;
    }
    if (isDuplicate) {
      return 'Email already exists';
    }
    if (duplicateError) {
      return 'Error checking email availability';
    }
    return '';
  }, [touched.emailID, errors.emailID, values.emailID, isDuplicate, duplicateError]);

  const isEmailError = Boolean(
    (touched.emailID && errors.emailID) || (values.emailID && isDuplicate) || (values.emailID && duplicateError)
  );

  // Profile picture state
  const [profilePic, setProfilePic] = useState<File | string | null>(null);
  const [profilePicPreview, setProfilePicPreview] = useState<string | null>(null);
  const [profilePicError, setProfilePicError] = useState<string | null>(null);
  const [userDeletedPic, setUserDeletedPic] = useState<boolean>(false); // Track if user manually deleted the pic
  const profilePicInputRef = useRef<HTMLInputElement>(null);

  // Update profile picture changed state when profilePic changes
  useEffect(() => {
    // Convert to boolean for easier comparison
    const hasCurrentPic = !!profilePic;
    const hadInitialPic = !!initialProfilePicRef.current;

    // If both are null/undefined, no change
    if (!hasCurrentPic && !hadInitialPic) {
      setProfilePicChanged(false);
      return;
    }

    // If exactly one is null (XOR - state changed), there's a change
    // This handles: deletion (had pic -> no pic) and upload (no pic -> has pic)
    if (hasCurrentPic !== hadInitialPic) {
      setProfilePicChanged(true);
      return;
    }

    // Both exist - compare values
    // If profilePic is a File, it's definitely a change
    if (profilePic instanceof File) {
      setProfilePicChanged(true);
      return;
    }

    // Both are strings (URLs), compare them
    if (typeof profilePic === 'string' && typeof initialProfilePicRef.current === 'string') {
      const changed = profilePic !== initialProfilePicRef.current;
      setProfilePicChanged(changed);
    }
  }, [profilePic]);

  // Validate profile pic file
  const validateProfilePicFile = (file: File): string | null => {
    if (file.size > 5000000) {
      return 'File size is too large (max 5MB)';
    }
    const validFileTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!validFileTypes.includes(file.type)) {
      return 'Unsupported file format. Please use JPEG, PNG, GIF, or SVG';
    }
    return null;
  };

  // Handle profile pic change
  const handleProfilePicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file) {
      const error = validateProfilePicFile(file);
      if (error) {
        setProfilePicError(error);
        setProfilePic(null);
        setProfilePicPreview(null);
        if (profilePicInputRef.current) profilePicInputRef.current.value = '';
        return;
      } else {
        setProfilePicError(null);
      }
      setProfilePic(file);
      setUserDeletedPic(false); // User uploaded new pic, reset deletion flag
      const reader = new FileReader();
      reader.onload = () => {
        setProfilePicPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      if (profilePicInputRef.current) profilePicInputRef.current.value = '';
    }
  };

  useEffect(() => {
    // CREATE MODE
    if (!userID) {
      setProfilePic(null);
      setProfilePicPreview(null);
      setUserDeletedPic(false);
      initialProfilePicRef.current = null;
      return;
    }

    if (profilePicUrl && !userDeletedPic) {
      initialProfilePicRef.current = profilePicUrl;
      setProfilePic(profilePicUrl);
      setProfilePicPreview(profilePicUrl);
    }

    if (userDeletedPic) {
      setProfilePic(null);
      setProfilePicPreview(null);
    }
  }, [userID, profilePicUrl, userDeletedPic]);

  const removeProfilePic = () => {
    setProfilePic(null);
    setProfilePicPreview(null);
    setProfilePicError(null);
    setUserDeletedPic(true);

    // FORCE mark as changed
    setProfilePicChanged(true);

    if (profilePicInputRef.current) profilePicInputRef.current.value = '';
  };

  const isSaveDisabled = useMemo(
    () =>
      Boolean(
        isSubmitting ||
        (values.emailID && isDuplicate) ||
        (values.emailID && duplicateError) ||
        !values.fullName ||
        !values.emailID ||
        values.userRoleIDs.length === 0 ||
        (userID && !formik.dirty && !profilePicChanged)
      ),
    [isSubmitting, values, isDuplicate, duplicateError, userID, formik.dirty, profilePicChanged]
  );

  const isRoleRemoved = useMemo(() => {
    const initialRoles = fromppfUserTypeFlag(latestUser?.ppfUserType);
    const currentRoles = values.ppfUserType || [];

    const removedPlanReviewer =
      initialRoles.includes(4) && !currentRoles.includes(4);

    const removedInspector =
      initialRoles.includes(8) && !currentRoles.includes(8);

    return removedPlanReviewer || removedInspector;
  }, [values.ppfUserType, latestUser]);

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate style={{ display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 48px)', minHeight: 0 }} onSubmit={(e) => {
        if (isSaveDisabled) {
          e.preventDefault();
          return;
        }
        handleSubmit(e);
      }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <Typography variant="h6" component="span">
            {userID ? (
              <>
                <Typography component="span" sx={{ color: 'primary.main' }}>
                  Edit
                </Typography>{' '}
                User
              </>
            ) : (
              <>
                <Typography component="span" sx={{ color: 'primary.main' }}>
                  New
                </Typography>{' '}
                User
              </>
            )}
          </Typography>
          <IconButton
            aria-label="close"
            onMouseDown={(e) => {
              e.preventDefault();
              handleClose();
              isClosingRef.current = true
            }}
            sx={{
              color: 'primary'
            }}
          >
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 2, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
          <Grid container spacing={2}>
            {/* Left Column: Profile Picture & Basic Status */}
            <Box sx={{ position: 'relative' }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="user-profile-pic"
                type="file"
                onChange={handleProfilePicChange}
                ref={profilePicInputRef}
              />
              <FormLabel
                htmlFor="user-profile-pic"
                sx={(theme) => ({
                  width: 100,
                  height: 70,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '10px',
                  border: `2px dashed ${theme.palette.primary.main}`,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  cursor: 'pointer',
                  overflow: 'hidden',
                  position: 'relative',
                  '&:hover .upload-overlay': {
                    backgroundColor: theme.palette.primary.lighter
                  }
                })}
              >
                {(profilePicPreview || profilePic) ? (
                  <Box
                    component="img"
                    src={profilePicPreview || (typeof profilePic === 'string' ? profilePic : '')}
                    alt="Profile"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <UserOutlined style={{ fontSize: 30, color: theme.palette.primary.main }} />
                )}

                <Box
                  className="upload-overlay"
                  sx={(theme) => ({
                    position: 'absolute',
                    inset: 0,
                    bgcolor: alpha(theme.palette.primary.main, 0.6),
                    opacity: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    transition: '0.3s'
                  })}
                >
                  <Stack spacing={0.5} alignItems="center">
                    <CameraOutlined style={{ fontSize: '1.5rem' }} />
                    <Typography variant="caption" fontWeight={600}>CHANGE</Typography>
                  </Stack>
                </Box>
              </FormLabel>

            </Box>

            <Stack spacing={1} alignItems="right" sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 400, color: 'text.secondary' }}>PNG/JPG, max size 2MB</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant='contained'
                  sx={{ height: '30px', borderRadius: '6px', fontSize: '12px' }}
                  startIcon={<UploadOutlined />}
                  onClick={() => profilePicInputRef.current?.click()}
                >Upload</Button>
                {/* {(profilePicPreview || profilePic) && (
                  <Button
                    sx={{ height: '30px', borderRadius: '6px', backgroundColor: theme.palette.grey[200], color: theme.palette.text.secondary }}
                    variant='contained'
                    onClick={removeProfilePic}
                  >Remove</Button>
                )} */}
                <Button
                  sx={{
                    height: '30px', fontSize: '12px', borderRadius: '6px', backgroundColor: theme.palette.grey[200], color: theme.palette.text.secondary, '&:hover': {
                      backgroundColor: theme.palette.grey[300],
                      color: theme.palette.text.primary
                      , fontSize: '12px'
                    }
                  }}
                  variant='contained'
                  onClick={removeProfilePic}
                  disabled={!(profilePicPreview || profilePic)}
                >Remove</Button>

              </Stack>
            </Stack>

            {/* Right Column: Form Fields */}
            <Grid size={{ xs: 12 }}>
              <Stack spacing={2}>
                {/* Personal Information Section */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Stack spacing={1}>
                      <InputLabel htmlFor="full-name" required>Full Name</InputLabel>
                      <TextField
                        autoFocus
                        required
                        fullWidth
                        id="full-name"
                        placeholder="Enter full name"
                        {...getFieldProps('fullName')}
                        error={Boolean((touched.fullName || submitCount > 0) && errors.fullName)}
                        helperText={(touched.fullName || submitCount > 0) && errors.fullName}
                        slotProps={{
                          input: {
                            inputProps: { maxLength: 100 }
                          }
                        }}
                      />
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <CommonPhoneInput
                      label="Mobile No"
                      id="phone"
                      name="phone"
                      value={values.phone}
                      onChange={(val) => setFieldValue('phone', val)}
                      onBlur={handleBlur}
                      error={Boolean((touched.phone || submitCount > 0) && errors.phone)}
                      helperText={(touched.phone || submitCount > 0) && errors.phone}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack spacing={1}>
                      <InputLabel htmlFor="user-email-id" required>Email</InputLabel>
                      <TextField
                        required
                        fullWidth
                        id="user-email-id"
                        placeholder="Enter email address"
                        {...getFieldProps('emailID')}
                        onBlur={(e) => {
                          if (isClosingRef.current) return;
                          formik.handleBlur(e);
                          const trimmedVal = e.target.value.trim();
                          const originalVal = (latestUser?.emailID || '').trim();
                          if (trimmedVal && trimmedVal !== originalVal && trimmedVal !== lastCheckedNameRef.current && !errors.emailID) {
                            if (timerRef.current) clearTimeout(timerRef.current);
                            checkDuplicate(trimmedVal);
                          }
                        }}
                        error={isEmailError}
                        helperText={getEmailError()}
                        slotProps={{
                          input: {
                            inputProps: { maxLength: 100, autoComplete: 'none' }
                          }
                        }}
                      />
                    </Stack>
                  </Grid>

                </Grid>
                {/* Role & Permissions Section */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <RoleLookup
                      name='userRoleIDs'
                      required
                      value={values.userRoleIDs}
                      error={Boolean(errors.userRoleIDs && !values.userRoleIDs ? true : false)}
                      helperText={typeof errors.userRoleIDs === 'string' && (touched.userRoleIDs || submitCount > 0) ? errors.userRoleIDs : undefined}
                      multiple={true}
                    />
                  </Grid>

                  {currentUser?.userType === 2 && (
                    <Grid size={{ xs: 12 }}>
                      <UserTypeLookup
                        name="ppfUserType"
                        value={values.ppfUserType}
                        onChange={(ids) => {
                          const isSalesRepSelected = ids?.includes(PPFUserType.SalesRep);
                          setFieldValue('isSalesRep', isSalesRepSelected);
                          setTimeout(() => {
                            formik?.validateField('ppfUserType');
                          }, 0);
                        }}
                      />
                    </Grid>
                  )}

                  {currentUser?.userType === 2 && values.ppfUserType?.includes(PPFUserType.PrivateProvider) && (
                    <Grid size={{ xs: 12 }}>
                      <Box
                        sx={{
                          width: '100%',
                          border: '1px solid #F0F0F0',
                          borderRadius: '6px',
                          p: 1.5
                        }}
                      >
                        <Typography sx={{ color: '#262626', fontFamily: 'Poppins, sans-serif', fontSize: 14, fontWeight: 500, mb: 1 }}>
                          Private Provider
                        </Typography>
                        <Stack spacing={0.75} sx={{ width: 300, maxWidth: '100%' }}>
                          <InputLabel htmlFor="license-number" required>
                            License Number
                          </InputLabel>
                          <TextField
                            id="license-number"
                            fullWidth
                            placeholder="Enter License Number"
                            {...getFieldProps('licenseNo')}
                            onBlur={(event) => {
                              setFieldValue('licenseNo', event.target.value.trim());
                              handleBlur(event);
                            }}
                            error={Boolean((touched.licenseNo || submitCount > 0) && errors.licenseNo)}
                            helperText={(touched.licenseNo || submitCount > 0) && errors.licenseNo}
                            slotProps={{
                              input: {
                                sx: {
                                  height: 44,
                                  borderRadius: '6px',
                                  fontFamily: 'Poppins, sans-serif',
                                  fontSize: 14,
                                  '& input': { p: '12px' },
                                  '& fieldset': { borderColor: '#D9D9D9', borderWidth: '1.2px' }
                                },
                                inputProps: { maxLength: 50 }
                              }
                            }}
                          />
                        </Stack>
                      </Box>
                    </Grid>
                  )}
                </Grid>


                {/* Sales Representative Section (Conditional) */}
                {values.isSalesRep && (
                  <MainCard
                    contentSX={{ p: 2 }}
                    sx={{ '& .MuiCardHeader-root': { p: 1.5 } }}
                    title={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1">Sales Representative Settings</Typography>
                      </Stack>
                    }
                  >
                    <Grid container spacing={2}>
                      {values.isSalesRep && (
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <Stack spacing={1}>
                            <InputLabel htmlFor="default-commission-perc" required>Default Commission %</InputLabel>
                            <TextField
                              fullWidth
                              placeholder="0.00"
                              {...getFieldProps('defaultCommissionPerc')}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const { value } = e.target;
                                if (/^\d*\.?\d{0,2}$/.test(value)) {
                                  const numValue = parseFloat(value);
                                  if (isNaN(numValue) || numValue <= 100) {
                                    formik.setFieldValue('defaultCommissionPerc', value);
                                  }
                                }
                              }}
                              slotProps={{
                                input: {
                                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                  inputProps: {
                                    inputMode: 'decimal',
                                    maxLength: 6
                                  }
                                }
                              }}
                              error={Boolean(touched.defaultCommissionPerc && errors.defaultCommissionPerc)}
                              helperText={touched.defaultCommissionPerc && errors.defaultCommissionPerc}
                            />
                          </Stack>
                        </Grid>
                      )}
                    </Grid>
                  </MainCard>
                )}
                {currentUser.userType === 2 && (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <UserLookup
                        name='supervisorUserID'
                        label='Supervisor'
                        placeholder="Select Supervisor"
                        value={values.supervisorUserID}
                        onChange={(value) => setFieldValue('supervisorUserID', value)}
                        showAddButton={false}
                        error={Boolean(touched.supervisorUserID && errors.supervisorUserID)}
                        helperText={touched.supervisorUserID ? (errors.supervisorUserID as string) : undefined}
                        excludeID={userID}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Lookup
                        lookupID="due-task-email-notification"
                        label="Due Task Email Notification"
                        placeholder="Select Due Task Email Notification"
                        value={dueTaskEmailNotificationOptions?.find((o) => o.value == values.dueTaskEmailNotification) || null}
                        dataSource={dueTaskEmailNotificationOptions || []}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        onChange={(newValue) => {
                          setFieldValue('dueTaskEmailNotification', newValue.value)
                        }}
                        showClearButton={false}
                      />
                    </Grid>
                  </Grid>
                )}

                <Stack spacing={4} sx={{ width: '100%' }} direction='row'>
                  {userID && (
                    <Box>
                      {/* Top Row */}
                      <Typography variant="body2" fontWeight={500}>
                        Account Status
                      </Typography>

                      {/* Bottom Row */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mt: 1 }}
                      >
                        {/* Left: Active Toggle */}
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Switch
                              checked={values.active}
                              onChange={(e) => setFieldValue('active', e.target.checked)}
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              color="primary.main"
                              fontWeight={500}
                            >
                              Active
                            </Typography>
                          }
                          labelPlacement="end"
                        />


                      </Stack>
                    </Box>
                  )}

                  {currentUser?.userType === 1 && userID && (
                    <Box >
                      {
                        values.twoFAEnabled ? (
                          <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="0.5" y="0.5" width="22" height="22" rx="3.5" fill="#F2FBF4" />
                            <rect x="0.5" y="0.5" width="22" height="22" rx="3.5" stroke="#E1F7E6" />
                            <path d="M16.7122 6.47642L11.7231 4.77619C11.6629 4.75564 11.5821 4.74536 11.5014 4.74536C11.4206 4.74536 11.3398 4.75564 11.2796 4.77619L6.29055 6.47642C6.16869 6.51753 6.06885 6.65848 6.06885 6.78769V13.8705C6.06885 13.9997 6.15254 14.17 6.25385 14.2508L11.3149 18.1945C11.3663 18.2341 11.4323 18.2547 11.4999 18.2547C11.5674 18.2547 11.635 18.2341 11.6849 18.1945L16.7459 14.2508C16.8472 14.1715 16.9309 14.0012 16.9309 13.8705V6.78769C16.9339 6.65848 16.834 6.519 16.7122 6.47642ZM15.8767 13.5886L11.5014 16.9979L7.12598 13.5886V7.31039L11.5014 5.81865L15.8767 7.31039V13.5886ZM9.91859 10.6374C9.87454 10.5772 9.80406 10.5405 9.72771 10.5405H8.91724C8.82181 10.5405 8.76601 10.6492 8.82181 10.727L10.6777 13.2817C10.6996 13.3118 10.7283 13.3362 10.7615 13.3531C10.7947 13.3699 10.8313 13.3787 10.8685 13.3787C10.9057 13.3787 10.9424 13.3699 10.9756 13.3531C11.0087 13.3362 11.0375 13.3118 11.0594 13.2817L14.1809 8.98418C14.2367 8.90637 14.1809 8.79772 14.0855 8.79772H13.275C13.2001 8.79772 13.1282 8.83442 13.0841 8.89462L10.8685 11.9456L9.91859 10.6374Z" fill="#38A34F" />
                          </svg>
                        ) : (
                          <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="0.5" y="0.5" width="22" height="22" rx="3.5" fill="#F5F5F5" />
                            <rect x="0.5" y="0.5" width="22" height="22" rx="3.5" stroke="#F0F0F0" />
                            <path d="M16.7122 6.47642L11.7231 4.77619C11.6629 4.75564 11.5821 4.74536 11.5014 4.74536C11.4206 4.74536 11.3398 4.75564 11.2796 4.77619L6.29055 6.47642C6.16869 6.51753 6.06885 6.65848 6.06885 6.78769V13.8705C6.06885 13.9997 6.15254 14.17 6.25385 14.2508L11.3149 18.1945C11.3663 18.2341 11.4323 18.2547 11.4999 18.2547C11.5674 18.2547 11.635 18.2341 11.6849 18.1945L16.7459 14.2508C16.8472 14.1715 16.9309 14.0012 16.9309 13.8705V6.78769C16.9339 6.65848 16.834 6.519 16.7122 6.47642ZM15.8767 13.5886L11.5014 16.9979L7.12598 13.5886V7.31039L11.5014 5.81865L15.8767 7.31039V13.5886ZM9.91859 10.6374C9.87454 10.5772 9.80406 10.5405 9.72771 10.5405H8.91724C8.82181 10.5405 8.76601 10.6492 8.82181 10.727L10.6777 13.2817C10.6996 13.3118 10.7283 13.3362 10.7615 13.3531C10.7947 13.3699 10.8313 13.3787 10.8685 13.3787C10.9057 13.3787 10.9424 13.3699 10.9756 13.3531C11.0087 13.3362 11.0375 13.3118 11.0594 13.2817L14.1809 8.98418C14.2367 8.90637 14.1809 8.79772 14.0855 8.79772H13.275C13.2001 8.79772 13.1282 8.83442 13.0841 8.89462L10.8685 11.9456L9.91859 10.6374Z" fill="#8C8C8C" />
                          </svg>
                        )
                      }
                      <Typography variant="body2" fontWeight={500} mt={0.5}>2FA is {values.twoFAEnabled ? 'enabled' : 'disabled'}</Typography>

                    </Box>
                  )}
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 1.5, flexShrink: 0, bgcolor: 'background.paper' }}>
          <Grid container justifyContent="space-between" alignItems="center" sx={{ width: 1 }}>
            <Grid>
              {values?.updatedOn && values?.updatedByUserName && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '9px' }}>
                  Edited by{' '}
                  <Typography component="span" sx={{ fontSize: '9px', fontStyle: 'italic', color: 'primary.main' }}>
                    {values.updatedByUserName}
                  </Typography>{' '}
                  at{' '}
                  <Typography component="span" sx={{ fontSize: '9px', fontStyle: 'italic', color: 'primary.main' }}>
                    {GetFormattedTimeOnly(new Date(values.updatedOn || ''))}
                  </Typography>{' '}
                  on{' '}
                  <Typography component="span" sx={{ fontSize: '9px', fontStyle: 'italic', color: 'primary.main' }}>
                    {GetFormattedDate(new Date(values.updatedOn || ''))}
                  </Typography>
                </Typography>
              )}
            </Grid>
            <Grid>
              <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
                <Button
                  type={userID && isRoleRemoved ? "button" : "submit"}
                  variant="contained"
                  color="primary"
                  onClick={() => userID && isRoleRemoved && setOpenDeleteRoleConfirmation(true)}
                  disabled={isSaveDisabled}
                  startIcon={
                    formik.isSubmitting ? (
                      <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                        <CircularProgress size={16} color="inherit" />
                      </motion.div>
                    ) : (
                      <SaveOutlined />
                    )
                  }
                >
                  Save
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </DialogActions>
        {openDeleteRoleConfirmation && (
          <RemoveRoleConfirmation
            open={openDeleteRoleConfirmation}
            onClose={() => setOpenDeleteRoleConfirmation(false)}
            selectedUserTypes={values.ppfUserType || []}
            ppfUserType={latestUser?.ppfUserType}
            onConfirm={() => {
              setOpenDeleteRoleConfirmation(false)
              handleSubmit()
            }}
          />
        )}
      </Form>
    </FormikProvider>
  );
};

export default UserForm;
